import { Flag } from "@phosphor-icons/react"
import { useMemo } from "react"

import { Badge, type SelectOption } from "~/components/design-system/components"
import { DEFAULT_RULE_COLOR, RULE_COLORS, type RuleColor } from "~/constants/ruleColors"
import type { VariantRule } from "~/server/api/routers/variantRules"
import { api } from "~/utils/api"
import { cn } from "~/utils/helpers"

const COLOR_LABEL: Record<RuleColor, string> = {
  yellow: "Yellow",
  red: "Red",
  green: "Green",
  blue: "Blue",
  violet: "Violet",
  neutral: "Grey",
}

// The badge's own text colour, so the dot matches the badge it previews.
const DOT_CLASS: Record<RuleColor, string> = {
  yellow: "bg-badge-orange-fg-text",
  red: "bg-badge-red-fg-text",
  green: "bg-badge-green-fg-text",
  blue: "bg-badge-blue-fg-text",
  violet: "bg-badge-violet-fg-text",
  neutral: "bg-badge-neutral-fg-text",
}

/** A rule's colour as a dot, where a whole badge would be too loud. */
export const RuleDot = ({ color }: { color: RuleColor }) => (
  <span className={cn("size-2 shrink-0 rounded-full", DOT_CLASS[color])} aria-hidden />
)

export const ruleColorOptions: SelectOption<RuleColor>[] = RULE_COLORS.map((color) => ({
  label: COLOR_LABEL[color],
  value: color,
  icon: <RuleDot color={color} />,
}))

/** A rule's flag: its name (or a count) in the rule's colour. */
export const RuleBadge = ({
  color,
  className,
  children,
}: {
  color: RuleColor
  className?: string
  children: string
}) => (
  <Badge variant={color} size="sm" className={cn("max-w-full min-w-0", className)}>
    <Flag size={11} className="shrink-0" />
    <span className="truncate">{children}</span>
  </Badge>
)

export type RuleById = ReadonlyMap<number, VariantRule>

/** Each rule by id, from the cached rules list. */
export function useRuleById(enabled = true): RuleById {
  const { data: rules } = api.variantRules.list.useQuery(undefined, { enabled })
  return useMemo(() => new Map(rules?.map((r) => [r.id, r])), [rules])
}

const SEVERITY: readonly RuleColor[] = ["red", "yellow", "green", "violet", "blue", "neutral"]

/** The most severe of several rules' colours, so tiered rules (green 10 %, yellow 20 %, red 30 %) show the highest tier hit. */
export const strongestRuleColor = (colors: readonly RuleColor[]): RuleColor =>
  SEVERITY.find((c) => colors.includes(c)) ?? DEFAULT_RULE_COLOR
